import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { instantFromWallClock, Size, lineEditorParts, lineEditorText, t, visitLineLabel, wallClockInZone, newDocumentTitle, type VisitLineOption } from '@bms2/shared';
import { ItemTable, isDivider, newLine, type ItemLineDraft } from '../../components/composites/ItemTable';
import { DatePicker, Dropdown, Select } from '../../components/controls';
import { ModalField } from '../../components/modals/ModalShell';
import { useDocumentToday, useViewerZone } from '../../shell/ViewerZone';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { ShipmentPreferencePicker } from './ShipmentPreferencePicker';
import { WORK_ORDERS_PATH, useWorkOrderOptions } from './WorkOrderPanels';
import { patch, post, put, useApi } from './api';
import { useCompanySearch, useItemPicker } from '../settings/api';
import { useAddNew } from './AddNewHost';
import { EMPTY_BOOKING, VisitBookingFields, bookingLines, fixedOf, fixedWaits, type Booking } from './WorkOrderVisits';
import { refreshMenuCounts } from '../../shell/menuCounts';

/** T295: where the priority tier refusal's link opens the editor. */
const PRIORITY_TIER_SETTINGS_ROUTE = '/settings/accounting/priority-tiers';

interface StoredWorkOrder {
  id: string;
  workOrderNumber: string;
  companyId: string;
  companyName: string;
  siteId: string;
  dealId: string | null;
  agreementId: string | null;
  scheduledStart: string | null;
  /** 🔒 The day the job is dated (decision 0681). */
  documentDate: string;
  /** 🔒 How the job's goods travel (decision 0682); absent from an older reply. */
  shipmentPreferenceId?: string | null;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); absent from an older reply. */
  customerNotes?: string | null;
  status: string;
  /** The labour lines, which the edit page's line editor reopens on (review item T141); absent from an older reply. */
  lines?: { id: string; description: string }[];
}

/**
 * 🔒 The Work Order's edit mode (decision 0409), on the job's own page rather
 * than in a modal. The customer and the site are the only required answers: the
 * tier derives from the notice and the origin from the assigned worker or the
 * Settings row. 🔨 The footer's second control schedules rather than sends,
 * because a Work Order's lifecycle carries no Send.
 */
export function WorkOrderForm({ workOrderId = null, apiBaseUrl = '' }: { workOrderId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  // 🔨 The viewer's zone comes from the shell's own read, never from the machine's clock (decision 0261).
  const zone = useViewerZone();
  const [params] = useSearchParams();
  const stored = useApi<StoredWorkOrder>(workOrderId ? `${WORK_ORDERS_PATH}/${workOrderId}` : null, apiBaseUrl);
  const document = stored.state.kind === 'ready' ? stored.state.data : null;

  const [chosen, setChosen] = useState<{ id: string; label: string } | null>(null);
  const fromUrl = params.get('companyId') ?? '';
  const customerId = document?.companyId ?? chosen?.id ?? fromUrl ?? null;
  const options = useWorkOrderOptions(customerId || null, apiBaseUrl);
  const answer = options.state.kind === 'ready' ? options.state.data : null;

  const [site, setSite] = useState(params.get('siteId') ?? '');
  // 🔒 The Deal a job is raised from arrives in the address beside the Account and the site (T1023, piece 22).
  const [dealId, setDealId] = useState(params.get('dealId') ?? '');
  const [agreementId, setAgreementId] = useState('');
  const [start, setStart] = useState('');
  /** 🔒 Decision 5570 (the owner's W5a): a new job's first visit, booked and sent with the job in one press; no Starts saves the job unbooked. */
  const [visit, setVisit] = useState<Booking>(EMPTY_BOOKING);
  // 🔒 A fresh job opens on one blank line, so the grid is typed into rather than found (review item T141; decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine(true)]);
  // 🔒 The job's own date (T114; decision 0681): a new job opens on today, a stored one on its own day.
  // 🔒 A new job opens on today in the viewer's own zone (decision 1030).
  const today = useDocumentToday();
  const [documentDate, setDocumentDate] = useState(today);
  const [shipmentPreference, setShipmentPreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [seen, setSeen] = useState<string | null>(null);
  if (document && seen !== document.id) {
    setSeen(document.id);
    setSite(document.siteId);
    setDealId(document.dealId ?? '');
    setAgreementId(document.agreementId ?? '');
    setDocumentDate(document.documentDate);
    setShipmentPreference(document.shipmentPreferenceId ?? '');
    setCustomerNotes(document.customerNotes ?? '');
    // The stored start is an instant; the field holds the wall clock it reads in the viewer's zone.
    setStart(document.scheduledStart ? wallClockInZone(document.scheduledStart, zone) : '');
    // A saved line reopens as a free-text row: the first line of its description is the name, the rest the notes under it.
    // 🔨 An older reply carries no lines and leaves the grid as it stands.
    if (document.lines) {
      const stored = document.lines.map((row): ItemLineDraft => {
        const parts = lineEditorParts(row.description);
        return { id: row.id, itemId: null, itemLabel: parts.label, description: parts.notes, qty: null, unit: '', rateCents: null, freeText: true };
      });
      setDrafts(stored.length > 0 ? stored : [newLine(true)]);
    }
  }
  /** 🔒 Decision 5571: the typed lines, in the order they are sent, which the first visit's lines tick. */
  const typedLines = drafts.filter((d) => !isDivider(d)).map((d) => ({ id: d.id, text: lineEditorText(d.itemLabel, d.description) })).filter((line) => line.text.length > 0);
  const lineOptions: VisitLineOption[] = typedLines.map((line, index) => ({ lineId: line.id, label: visitLineLabel(index + 1, line.text.split('\n')[0] ?? '') }));
  const searchCompanies = useCompanySearch(apiBaseUrl, 'customer');
  const { itemSearch, descriptions } = useItemPicker(apiBaseUrl, 'sales');

  const save = async (then: 'view' | 'schedule'): Promise<void> => {
    if (!customerId) return setError(t('workOrder.new.customer'));
    // A Work Order's lines keep no groups, so a divider is refused by name rather than dropped on the floor.
    if (drafts.some(isDivider)) return setError(t('workOrder.form.noDividers'));
    if (!site) return setError(t('workOrder.new.site'));
    // 🔒 T3621: on the new job's form, a Deal from the address that is not one of this customer's is refused here, in words, before the server's.
    // 🔨 A stored job keeps its Deal: the options list leaves out a Lost Deal and stops at fifty, so the server's own check is the edit form's.
    if (!workOrderId && dealId && answer && !answer.deals.some((row) => row.id === dealId)) return setError(t('workOrder.new.dealNotTheirs'));
    // 🔨 The field holds a wall clock; the instant it names in the viewer's zone is what the server stores (decision 0261).
    let scheduledStart: string | null = null;
    if (start.trim()) {
      try {
        scheduledStart = instantFromWallClock(start.trim(), zone).toISOString();
      } catch {
        // silent: a start that is not a time is refused with a sentence, a refusal of what was typed.
        return setError(t('workOrder.new.startInvalid'));
      }
    }
    // 🔒 Decision 5570: a new job is one press, the job, its lines and its first visit with the crew written together.
    if (!workOrderId) return createWhole();
    setBusy(true);
    const body = { companyId: customerId, siteId: site, dealId: dealId || null, agreementId: agreementId || null, scheduledStart, documentDate: documentDate || null, shipmentPreferenceId: shipmentPreference || null, customerNotes };
    const saved = workOrderId
      ? await patch<{ id: string }>(`${WORK_ORDERS_PATH}/${workOrderId}`, body, apiBaseUrl)
      : await post<{ id: string }>(WORK_ORDERS_PATH, body, apiBaseUrl);
    if (!saved.ok) {
      setBusy(false);
      return setError(saved.message);
    }
    const id = workOrderId ?? saved.data.id;
    // 🔒 The whole set of lines saves at once; a stored line keeps its workers and its completion (review item T141).
    const lines = drafts
      .filter((d) => !isDivider(d))
      .map((d) => ({ ...(d.id.startsWith('line-') ? {} : { id: d.id }), description: lineEditorText(d.itemLabel, d.description) }))
      .filter((d) => d.description.length > 0);
    const wroteLines = await put(`${WORK_ORDERS_PATH}/${id}/lines`, { lines }, apiBaseUrl);
    setBusy(false);
    if (!wroteLines.ok) return setError(wroteLines.message);
    // 🔨 A Work Order's first outward move is the booking; the schedule asks its own question on the saved job.
    navigate(`/work-orders/${id}${then === 'schedule' ? '?act=schedule' : ''}`);
  };

  const createWhole = async (): Promise<void> => {
    if (!visit.startsAt && (visit.workers.length > 0 || visit.endsAt)) return setError(t('workOrder.booking.needsStart'));
    if (visit.startsAt && fixedWaits(visit)) return setError(t('workOrder.booking.fixedNeedsReason'));
    const lines = typedLines.map((line) => line.text);
    // 🔒 Decision 5571: the visit's lines and each worker's, by their place among the lines sent, since none exists yet.
    const chosen = bookingLines(visit, lineOptions);
    const place = (ids: readonly string[]) => ids.map((id) => typedLines.findIndex((line) => line.id === id)).filter((index) => index >= 0);
    const firstVisit = visit.startsAt
      ? {
        startsAt: visit.startsAt, endsAt: visit.endsAt || null, timeZone: zone, workerEmploymentIds: visit.workers.map((chip) => chip.id), leadEmploymentId: visit.lead || null, ...fixedOf(visit),
        ...(chosen.visitLineIds ? { visitLineIndexes: place(chosen.visitLineIds) } : {}),
        ...(chosen.workerLineIds ? { workerLineIndexes: Object.fromEntries(Object.entries(chosen.workerLineIds).map(([employmentId, ids]) => [employmentId, place(ids)])) } : {}),
      }
      : null;
    setBusy(true);
    const saved = await post<{ id: string }>(WORK_ORDERS_PATH, { companyId: customerId, siteId: site, dealId: dealId || null, agreementId: agreementId || null, documentDate: documentDate || null, shipmentPreferenceId: shipmentPreference || null, customerNotes, lines, firstVisit }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    // A booked visit moves the Dispatch Board's and My Jobs' balloons; an unbooked job joins Unscheduled.
    refreshMenuCounts();
    navigate(`/work-orders/${saved.data.id}`);
  };

  return (
    <>
    <DocumentEditPage
      title={document?.workOrderNumber ?? newDocumentTitle('WorkOrder')}
      subtitle={document?.companyName ?? chosen?.label ?? ''}
      outwardLabel={t(workOrderId ? 'document.edit.saveAndSchedule' : 'settings.save')}
      busy={busy}
      error={error}
      onDismissError={() => setError(null)}
      // 🔨 T295: the priority tier's own refusal wording carries a link to the editor, rather than the default dismiss.
      errorAction={error === t('workOrder.priorityTier.missing') ? { label: t('workOrder.priorityTier.open'), onAction: () => navigate(PRIORITY_TIER_SETTINGS_ROUTE) } : null}
      // 🔒 Decision 5570: a new job has the one Save, which books the visit when Starts is filled.
      onSaveDraft={workOrderId ? () => void save('view') : null}
      onSaveAndSend={() => void save('schedule')}
      onCancel={() => navigate(workOrderId ? `/work-orders/${workOrderId}` : '/work-orders')}
      after={workOrderId ? null : {
        heading: t('workOrder.booking.firstVisit'),
        body: (
          <>
            <p className={styles.hint} data-work-order-first-visit-hint="true">{t('workOrder.booking.firstVisitHint')}</p>
            <VisitBookingFields booking={visit} onChange={setVisit} zone={zone} apiBaseUrl={apiBaseUrl} companyId={customerId || null} name="first-visit" whoCanGo={site ? { siteId: site, workOrderId: null } : null} startRequired={false} lines={lineOptions} />
          </>
        ),
      }}
      header={
        <>
          {document || fromUrl ? (
            <ModalField label={t('workOrder.new.customer')}>
              <span data-work-order-customer={customerId ?? ''}>{document?.companyName ?? chosen?.label ?? ''}</span>
            </ModalField>
          ) : (
            <Dropdown size={Size.Medium} label={t('workOrder.new.customer')} entityLabel={t('workOrder.new.customer')} search={searchCompanies} value={chosen?.label ?? null} onSelect={(row) => { setChosen({ id: row.id, label: row.line1 }); setSite(''); setDealId(''); }} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'customer' }, (row) => { setChosen({ id: row.id, label: row.line1 }); setSite(''); setDealId(''); })} required />
          )}
          <Select size={Size.Medium} label={t('workOrder.new.site')} value={site} onChange={setSite} placeholder={t('workOrder.new.noneYet')} options={(answer?.sites ?? []).map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-new-work-order-site': 'true' }} required />
          <div data-work-order-field="date">
            <DatePicker size={Size.Small} label={t('document.edit.date')} value={documentDate} onChange={setDocumentDate} mode="date" name="work-order-date" />
          </div>
          <Select size={Size.Medium} label={t('workOrder.new.deal')} value={dealId} onChange={setDealId} placeholder={t('workOrder.new.noneYet')} options={(answer?.deals ?? []).map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-new-work-order-deal': 'true' }} />
          <Select size={Size.Medium} label={t('workOrder.new.agreement')} value={agreementId} onChange={setAgreementId} placeholder={t('workOrder.new.noneYet')} options={(answer?.agreements ?? []).map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-new-work-order-agreement': 'true' }} />
          <ShipmentPreferencePicker value={shipmentPreference} onChange={setShipmentPreference} apiBaseUrl={apiBaseUrl} name="workOrder" />
          {workOrderId ? <DatePicker size={Size.Medium} label={t('workOrder.new.start')} value={start} onChange={setStart} mode="datetime" name="work-order-start" /> : null}
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="workOrder" />
        </>
      }
      lines={
        <>
          <p className={styles.hint} data-work-order-lines-hint="true">{t('workOrder.form.linesHint')}</p>
          <ItemTable
            documentType="WorkOrder"
            // 🔒 A labour line is its description: the rate is each worker's, set on the saved job, so the grid asks for the work alone.
            columns={['item']}
            columnLabels={{ item: t('workOrder.line.work') }}
            lines={drafts}
            onChange={setDrafts}
            discountLevel="Footer"
            itemSearch={itemSearch}
            descriptionOf={(id) => descriptions.current.get(id) || null}
          />
        </>
      }
    />
    {addNew.host}
    </>
  );
}
